import { Component, OnInit, InjectionToken, OnDestroy, Inject, Optional } from '@angular/core';
import { Observable, Subject, combineLatest } from 'rxjs';
import { TableOfContents, PersistenceService, PersistedDate, FormControls, ManifestSettings, DependentValidations, TranslationFactory } from './types';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { shareReplay, tap, map, filter, takeUntil, withLatestFrom, switchMap, startWith, debounceTime, take, mapTo } from 'rxjs/operators';
import {isValidNumber, parse} from 'libphonenumber-js';
import { isEqual, uniq, intersection } from 'lodash/fp';
import { escapeRegExp } from '@angular/compiler/src/util';
import { ValidatorFn, AbstractControl } from '@angular/forms';
import { UrlSegment } from '@angular/router';
import { storage } from 'firebase/app';

export { TableOfContents, PersistenceService, PersistedDate, TranslationFactory };

export const TRANSLATION_FACTORY = new InjectionToken<TranslationFactory>("ngx-questionnaire-core.translate-fn");
export const TABLE_OF_CONTENTS = new InjectionToken<Observable<TableOfContents>>("ngx-questionnaire-core.table_of_contents");
export const PERSISTENCE_SERVICE = new InjectionToken<PersistenceService>("ngx-questionnaire-core.persistence_service");
export const REDIRECT = new InjectionToken<any[]>("ngx-questionnaire-core.redirect");
export const AUTO_SAVE_INTERNAL = new InjectionToken<number|undefined>("ngx-questionnaire-core.autosave_interval");
export const DEBUG_MODE = new InjectionToken<boolean>("ngx-questionnaire-core.debug_mode");

const DIGITS_ONLY_REGEX = /^[0-9]+$/i

const PHONE_COUNTRY = 'US';

function validPhone(value: any) {
  return isValidNumber(parse(value || '', PHONE_COUNTRY));
}

const phoneNumberValidator: ValidatorFn = (control: AbstractControl) => {
  return validPhone(control.value) ? null : {validPhone: true};
}

const requireAnyValidator: ValidatorFn = (control: any) => {
  return Object.values(control.controls).some((it: any) => !!it.value) ? null : {'required': true};
}

/**
 * Validator that can be attached to a froup group and asserts two form fields
 * are equal
 *
 *   const a = new FormControl('');
 *   const b = new FormControl('');
 *   const group = new FormGroup({a, b}, [controlsMustBeTheSame(a, b)]);
 */
function controlsMustBeTheSame(
  a: AbstractControl, b: AbstractControl, comparator = isEqual): ValidatorFn {
return (group: AbstractControl) => {
  const matches = comparator(a.value, b.value);
  if (!matches) {
    b.setErrors({mustBeTheSame: true})
  }
  return matches ? null : {'error': true};
};
}

const buildValidations = (settings: ManifestSettings) => [
  settings.required && settings.type !== 'checkbox' && Validators.required,
  settings.required && settings.type === 'checkbox' && (!settings.options || settings.options.length === 1) && Validators.pattern("true"),
  settings.required && settings.type === 'checkbox' && settings.options && settings.options.length > 1 && requireAnyValidator,
  settings.max && settings.type !== 'date' && Validators.max(settings.max),
  settings.min && settings.type !== 'date' && Validators.min(settings.min),
  settings.max && settings.type === 'date' && maxDateValidator(settings.max),
  settings.min && settings.type === 'date' && minDateValidator(settings.min),
  settings.maxLength && Validators.min(settings.maxLength),
  settings.minLength && Validators.min(settings.minLength),
  settings.regexp && Validators.pattern(settings.regexp),
  settings.type === 'number' && Validators.pattern(DIGITS_ONLY_REGEX),
  settings.type === 'email' && Validators.email,
  settings.type === 'tel' && phoneNumberValidator,
].filter(it => !!it).map(x => x as ValidatorFn)

@Component({
  selector: 'lib-ngx-questionnaire',
  templateUrl: './questionnaire.component.html',
  styleUrls: ['./questionnaire.component.css'],
})
export class QuestionnaireComponent implements OnInit, OnDestroy {

  readonly tableOfContents$: Observable<TableOfContents>;
  readonly question$: Observable<TableOfContents>;
  readonly page$: Observable<string>;
  readonly showRequiredError$ = new Subject<boolean>();
  readonly control$: Observable<FormControls>;
  readonly formGroup$: Observable<FormGroup>;
  readonly value$: Observable<any>;
  readonly saving$: Observable<boolean>;
  readonly showError$ = new Subject<boolean>();

  readonly uniquePage$: Observable<string[]>;
  readonly allPage$: Observable<string[]>;
  private readonly componentDestroyed$ = new Subject<boolean>();

  private readonly previousPage$: Observable<string|undefined>;
  private readonly nextPage$: Observable<string|undefined>;

  readonly showPrevious$: Observable<boolean>;
  readonly showNext$: Observable<boolean>;
  readonly showSubmit$: Observable<boolean>;

  private shouldResume: boolean = true;
  
  lastError: any;

  constructor(
    @Inject(TABLE_OF_CONTENTS) manifest: Observable<TableOfContents>,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    @Inject(PERSISTENCE_SERVICE) private readonly persistenceService: PersistenceService,
    @Inject(REDIRECT) private readonly redirect: any[],
    @Inject(TRANSLATION_FACTORY) private readonly _translate: TranslationFactory,
    @Optional() @Inject(AUTO_SAVE_INTERNAL) private readonly autoSaveInterval: number|undefined,
    private readonly formBuilder: FormBuilder,
    @Optional() @Inject(DEBUG_MODE) public readonly debugMode: boolean = false,
  ) {

    this.tableOfContents$ = manifest.pipe(shareReplay({ bufferSize: 1, refCount: false }));

    this.page$ = activatedRoute.url.pipe(
      tap(() => this.showRequiredError$.next(false)),
      map(this.urlSegmentToPage),
      filter(it => !!it)
    );

    this.value$ = this.persistenceService.value$;

    this.saving$ = this.persistenceService.saving$;

    this.question$ = combineLatest(this.page$, this.tableOfContents$).pipe(
      map(([page, manifest]) => manifest.filter(([_, settings]) => settings.page === page)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.control$ = this.question$.pipe(
      takeUntil(this.componentDestroyed$),
      map(questions => questions.reduce((acc, [key, settings]) => {
        const validators = settings.dependentOn ? [] : buildValidations(settings); // TODO fix the filter type & drop map
        const multiCheckbox = settings.type === 'checkbox' && settings.options && settings.options.length > 1;
        if (multiCheckbox) {
          const group = this.formBuilder.group(settings.options!.reduce((acc, option) => ({...acc, [option]: new FormControl(null)})), { validators });
          return {...acc, [key]: group};
        } else {
          const control = new FormControl(settings.default, validators);
          return {...acc, [key]: control };
        }
        
      }, {} as {[key:string]:FormControl|FormGroup})),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.formGroup$ = this.control$.pipe(
      withLatestFrom(this.question$),
      map(([controls, questions]) => {
        const validations: ValidatorFn[] = questions
          .filter(([ , settings]) => settings.confirmationOf)
          .map(([key, settings]) =>
            controlsMustBeTheSame(controls[settings.confirmationOf!], controls[key], isEqual)
          );
        return new FormGroup(controls, validations);
      }),
      withLatestFrom(this.page$),
      switchMap(([formGroup, page]) => this.persistenceService.restore(page, formGroup).then(() => formGroup)),
      tap(formGroup => formGroup.updateValueAndValidity()),
      shareReplay({ bufferSize: 1, refCount: false })
    )

    this.uniquePage$ = this.tableOfContents$.pipe(
      map(toc => uniq(toc.map(([, { page }]) => page))),
    )

    this.allPage$ = this.formGroup$.pipe(
      switchMap(form => form.valueChanges.pipe(startWith(form.value))),
      withLatestFrom(this.page$, this.control$, this.tableOfContents$, this.value$),
      map(([, page, controls, manifest, data]) => {
        const filteredQuestions = manifest.filter(([key, settings]) => {
          if (!settings.dependentOn || settings.page === page) { return true; }
          return this.showField(key, settings, controls, data, manifest)
        });
        return uniq(filteredQuestions.map(([, { page }]) => page));
      })
    );
  
    const pageIndex = combineLatest(this.page$, this.allPage$).pipe(map(([page, allPages]) => allPages.indexOf(page)));
    this.previousPage$ = combineLatest(pageIndex, this.allPage$).pipe(map(([index, allPages]) => allPages[index - 1]));
    this.nextPage$ = combineLatest(pageIndex, this.allPage$).pipe(map(([index, allPages]) => allPages[index + 1]));
    this.showPrevious$ = this.previousPage$.pipe(map(previousPage => !!previousPage));
    this.showNext$ = this.nextPage$.pipe(map(nextPage => !!nextPage));
    this.showSubmit$ = this.showNext$.pipe(map(showNext => !showNext));

  }

  translate(...args: any[]): Observable<string> {
    return this._translate(...args).pipe(this.debugMode ? map(it => it || args.join('.')) : tap());
  }

  private urlSegmentToPage(url: UrlSegment[]) {
    return url.map(segment => segment.path).join('/');
  }

  get page() {
    return this.urlSegmentToPage(this.activatedRoute.snapshot.url);
  }

  ngOnInit(): void {

    this.activatedRoute.url.pipe(
      takeUntil(this.componentDestroyed$),
      tap(() => {
        if (this.shouldResume) {
          this.shouldResume = false;
          this.persistenceService.resume();
        }
      }),
    ).subscribe();

    if (this.autoSaveInterval) {
      this.formGroup$.pipe(
        switchMap(form => form.valueChanges),
        debounceTime(this.autoSaveInterval),
        withLatestFrom(this.page$),
        switchMap(([, page]) => this.save(page))
      ).subscribe();
    }

    this.formGroup$.pipe(
      switchMap(form => form.valueChanges.pipe(startWith(form.value))),
      withLatestFrom(this.control$, this.question$, this.value$),
      tap(([, controls, questions, data]) =>
        questions.forEach(([key, settings]) => {
          const control = controls[key];
          if (control && settings.dependentOn) {
            if (this.showField(key, settings, controls, data)) {
              const validators = buildValidations(settings);
              control.setValidators(validators);
              control.updateValueAndValidity({emitEvent: false});
            } else {
              control.clearValidators();
              control.setValue(null, {emitEvent: false});
              control.updateValueAndValidity({emitEvent: false});
            }
          }
        })
      )
    ).subscribe()

  }

  showField(key: string, settings: ManifestSettings, controls: FormControls, data: any, manifest?: TableOfContents) {
    if (!settings.dependentOn) { return true; }
    return settings.dependentOn.every(([dependentOn, validations]) => {
      const parent = controls[dependentOn];
      if (parent && !parent.valid) { return false }
      // answersToDelete might have wiped out some fields, but there's a race condition between flipping
      // to the new page and firestore returning latest data; need to take into account the deletes for next page calc  
      const answersToSkip = manifest ? this.answersToDelete(controls, manifest) : [];
      const dkeys = Object.keys(data || {});
      if (answersToSkip.includes(dependentOn) || !parent && !dkeys.includes(dependentOn)) { return false }
      // Can't express undefined in JSON, so we need to fallback to null
      const value = ((parent ? parent.value : data && data[dependentOn]) ?? null);
      return this.showFieldTest(validations, value);
    });
  }

  private showFieldTest(validations: DependentValidations|undefined, value: any) {
    const vkeys = Object.keys(validations || {});
    if (validations && vkeys.length) {
      return (vkeys.includes("greaterThan") ? value > validations.greaterThan! : true) &&
            (vkeys.includes("lessThan") ? value < validations.lessThan! : true) &&
            (vkeys.includes("regexp") ? value.match(new RegExp(escapeRegExp(validations.regexp!))) : true) &&
            (vkeys.includes("equals") ? isEqual(value, validations.equals) : true) &&
            (vkeys.includes("notEquals") ? !isEqual(value, validations.notEquals) : true);
    } else {
      return value !== null && value !== "";
    }
  }

  private answersToDelete(controls: FormControls, manifest: TableOfContents) {
    let currentPage: string;
    let answersToDelete: string[] = [];
    Object.keys(controls).forEach(key => {
      const settings = manifest.find(it => it[0] === key)![1];
      currentPage = settings.page;
      const moreAnswersToDelete = manifest.filter(it => it[1].dependentOn)
                                          .filter(it => !answersToDelete.includes(it[0]))
                                          .filter(it => it[1].page !== currentPage)
                                          .filter(it => !it[1].dependentOn!
                                                             .filter(it => it[0] === key)
                                                             .every(([,validations]) =>
                                                               this.showFieldTest(validations, controls[key].value)
                                                             ))
                                          .map(it => it[0]);
      answersToDelete = answersToDelete.concat(moreAnswersToDelete);
    });
    while (true) {
      const moreAnswersToDelete = manifest.filter(it => it[1].dependentOn)
                                          .filter(it => !answersToDelete.includes(it[0]))
                                          .filter(it => it[1].page !== currentPage)
                                          .filter(it => intersection(answersToDelete, it[1].dependentOn!.map(it => it[0])).length)
                                          .map(it => it[0]);
      if (!moreAnswersToDelete.length) { break }
      answersToDelete = answersToDelete.concat(moreAnswersToDelete);
    }
    return answersToDelete;
  }

  private save(lastPage: string, finished: boolean=false) {
    return this.formGroup$.pipe(
      tap(() => this.showRequiredError$.next(true)),
      withLatestFrom(this.control$, this.tableOfContents$),
      switchMap(([form, controls, manifest]) => 
        this.persistenceService.save(lastPage, form, this.answersToDelete(controls, manifest), finished).then(() => {
          form.markAsPristine();
          if (form.valid) {
            return true;
          } else {
            return null;
          }
        }).catch((e: any) => {
          this.lastError = e;
          return false;
        })
      ),
      take(1)
    );
  }

  dismissError() {
    this.showError$.next(false);
    this.lastError = undefined;
  }

  next() {
    return this.formGroup$.pipe(
      switchMap(form => form.valid ? this.nextPage$ : this.page$),
      take(1),
      switchMap(page => this.save(page!).pipe(map(it => [it, page]))),
      tap(([success,page]) => {
        if (success) {
          this.router.navigate([page], { relativeTo: this.activatedRoute.parent })
        } else if (success === false) {
          this.showError$.next(true);
        }
      })
    ).toPromise().catch((e:any) => {
      this.lastError = e;
      this.showError$.next(true);
    });
  }

  previous() {
    return this.previousPage$.pipe(
      take(1),
      switchMap(page => this.save(page!).pipe(map(it => [it, page]))),
      tap(([success,page]) => {
        if (success === false) {
          this.showError$.next(true);
        } else {
          this.router.navigate([page], { relativeTo: this.activatedRoute.parent })
        }
      })
    ).toPromise().catch((e:any) => {
      this.lastError = e;
      this.showError$.next(true);
    });
  }

  submit() {
    return this.page$.pipe(
      take(1),
      withLatestFrom(this.formGroup$),
      switchMap(([page, form]) => this.save(page!, form.valid)),
      tap(success => success && this.router.navigate(this.redirect))
    ).toPromise().catch((e:any) => {
      this.lastError = e;
      this.showError$.next(true);
    });
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
  }

  className(key: string) {
    return (`form-group form-group-${key}`);
  }

  formControl(key: string, control: any, settings: ManifestSettings) {
   return settings.options!.length > 1 ? control.controls[key] : control
  }

  public readonly fileUploads: Record<string, Observable<storage.UploadTaskSnapshot>> = {};
  private readonly fileUploadAborts: Record<string, Subject<boolean>> = {};

  onSelectedFilesChanged(key: string, control:FormControl, fileList:FileList|null) {
    if (this.fileUploadAborts[key]) {
      this.fileUploadAborts[key].next(true);
      delete this.fileUploads[key];
    }
    control.setValue(null);
    if (fileList && fileList.length > 0) {
      this.fileUploadAborts[key] = new Subject();
      // TODO handle multiple uploads
      const file = fileList[0];
      this.fileUploads[key] = this.persistenceService.upload(file).pipe(
        takeUntil(this.fileUploadAborts[key]),
        tap(snap => {
          if (snap.state === storage.TaskState.SUCCESS) {
            control.setValue(`gs://${snap.metadata.bucket}/${snap.metadata.fullPath}`);
          }
        }),
      );
    }
  }

}

function minDateValidator(minDate: any): ValidatorFn {
  let minDateDate = new Date(
    "" + minDate.year + "-" + minDate.month + "-" + minDate.day
  );

  return (control: AbstractControl) => {
    let controlDate: Date;
    if (control.value && typeof control.value.year == "number") {
      controlDate = new Date(
        "" +
          control.value.year +
          "-" +
          control.value.month +
          "-" +
          control.value.day
      );
    } else {
      controlDate = control.value;
    }
    return controlDate && controlDate >= minDateDate
      ? null
      : { 'minDate': true };
  };
}

function maxDateValidator(maxDate: any): ValidatorFn {
  let maxDateDate = new Date(
    "" + maxDate.year + "-" + maxDate.month + "-" + maxDate.day
  );

  return (control: AbstractControl) => {
    let controlDate: Date;
    if (control.value && typeof control.value.year == "number") {
      controlDate = new Date(
        "" +
          control.value.year +
          "-" +
          control.value.month +
          "-" +
          control.value.day
      );
    } else {
      controlDate = control.value;
    }
    return controlDate && controlDate <= maxDateDate
      ? null
      : { 'maxDate': true };
  };
}